import { useCallback, useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../store';
import {
  setThemePreference,
  ThemePreference,
} from '../store/slices/theme.slice';
import { ThemeMode } from '../theme/theme';

const MEDIA_QUERY = '(prefers-color-scheme: dark)';

const getSystemMode = (): ThemeMode => {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return 'light';
  }
  return window.matchMedia(MEDIA_QUERY).matches ? 'dark' : 'light';
};

export const useTheme = () => {
  const dispatch = useAppDispatch();
  const preference = useAppSelector((state) => state.theme.preference);
  const [systemMode, setSystemMode] = useState<ThemeMode>(getSystemMode);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(MEDIA_QUERY);
    const handler = (event: MediaQueryListEvent) =>
      setSystemMode(event.matches ? 'dark' : 'light');
    mql.addEventListener?.('change', handler);
    return () => mql.removeEventListener?.('change', handler);
  }, []);

  const effectiveMode: ThemeMode =
    preference === 'system' ? systemMode : preference;

  const setPreference = useCallback(
    (next: ThemePreference) => dispatch(setThemePreference(next)),
    [dispatch],
  );

  const toggle = useCallback(() => {
    dispatch(setThemePreference(effectiveMode === 'dark' ? 'light' : 'dark'));
  }, [dispatch, effectiveMode]);

  return {
    preference,
    effectiveMode,
    isDark: effectiveMode === 'dark',
    setPreference,
    toggle,
  };
};
